import { type FC } from 'react';
import { BaseEdge, type EdgeProps } from '@xyflow/react';

/**
 * Orthogonal edge that routes through a horizontal channel at a specified Y coordinate.
 * Used for empty condition branches connecting to Default Actions.
 *
 * Path: source → down to channelY → horizontal to targetX → down to target.
 * Rounded corners at each bend for clean visuals.
 */
export const ChannelEdge: FC<EdgeProps> = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  style,
  markerEnd,
  data,
}) => {
  const edgeData = data as Record<string, unknown> | undefined;
  const channelY = (edgeData?.channelY as number) ?? (sourceY + targetY) / 2;
  const isDashed = edgeData?.dashed === true;
  const r = 10;

  let path: string;

  if (Math.abs(sourceX - targetX) < 1) {
    // Vertically aligned: straight line
    path = `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
  } else {
    const dx = targetX - sourceX;
    const dir = dx > 0 ? 1 : -1;
    const cr = Math.max(
      0,
      Math.min(
        r,
        Math.abs(dx) / 2,
        Math.abs(channelY - sourceY) / 2,
        Math.abs(targetY - channelY) / 2
      )
    );

    path = [
      `M ${sourceX} ${sourceY}`,
      `L ${sourceX} ${channelY - cr}`,
      `Q ${sourceX} ${channelY} ${sourceX + dir * cr} ${channelY}`,
      `L ${targetX - dir * cr} ${channelY}`,
      `Q ${targetX} ${channelY} ${targetX} ${channelY + cr}`,
      `L ${targetX} ${targetY}`,
    ].join(' ');
  }

  const finalStyle = {
    ...style,
    ...(isDashed ? { strokeDasharray: '6 4' } : {}),
  };

  return <BaseEdge path={path} style={finalStyle} markerEnd={markerEnd} />;
};
